import React from 'react'
import { LmsIcon } from './lms-icon'

// Shared display helpers for the LMS admin surfaces (docs/08). Logo
// monograms and status colors follow the design-system artifacts.

export const logoFor = presetName => {
  const name = (presetName || '').toLowerCase()
  if (name.includes('moodle')) return { className: 'moodle', text: 'M' }
  if (name.includes('edx')) return { className: 'edx', text: 'edX' }
  if (name.includes('cornerstone')) return { className: 'cornerstone', text: 'CS' }
  if (name.includes('workday')) return { className: 'workday', text: 'WD' }
  if (name.includes('sap') || name.includes('successfactors')) return { className: 'sap', text: 'SAP' }
  return { className: 'custom', text: <LmsIcon name="code" /> }
}

// A connection's status as the admin words it: the Connections list's pill and
// the connection page's control read the same words and tones (a pill is green
// while it launches, amber while paused, grey once archived). `rank` orders the
// list's sort: Active, Paused, Archived.
export const CONNECTION_STATUS = {
  active: { tone: 'ok', label: 'Active', rank: 0 },
  paused: { tone: 'warn', label: 'Paused', rank: 1 },
  archived: { tone: null, label: 'Archived', rank: 2 }
}

export const connectionStatus = status => CONNECTION_STATUS[status] || CONNECTION_STATUS.archived

export const domainOf = url => {
  try {
    return new URL(url).host
  } catch {
    return url
  }
}

// The origin of an LMS address as the server reads it (lowercase
// scheme://host[:port], the default port dropped, no path), or null when the
// address has none. It issues the learner ids of the connection's launches, so
// a change of origin is a question the admin must answer (plan Task 14A fix 1).
export const originOf = url => {
  try {
    const { protocol, origin } = new URL(String(url).trim())
    return protocol === 'http:' || protocol === 'https:' ? origin : null
  } catch {
    return null
  }
}

// What an address carries besides its origin (CP2 review R1): 'wildcard',
// 'credentials' (a user name or password), 'fragment', 'query' or 'path' (one
// other than none or "/"), or null when it carries none of them. The service
// refuses a cmi5 allowed entry with any of them rather than storing its origin.
export const originExtra = url => {
  const raw = String(url).trim()
  if (raw.includes('*')) return 'wildcard'
  let parsed
  try {
    parsed = new URL(raw)
  } catch {
    return null
  }
  const authority = /^[a-z][a-z0-9+.-]*:\/\/([^/?#\\]*)/i.exec(raw)
  if (parsed.username || parsed.password || (authority && authority[1].includes('@'))) return 'credentials'
  if (parsed.hash || raw.includes('#')) return 'fragment'
  if (parsed.search || raw.includes('?')) return 'query'
  if (parsed.pathname !== '/' && parsed.pathname !== '') return 'path'
  return null
}

// What to tell an admin who typed an origin with more than its origin in it
// (originExtra's answers), for the lists that hold origins only: the cmi5
// allowed home pages and a connection's allowed launch origins.
const ONLY_ORIGIN = 'enter the origin only, such as https://lms.example.com.'
export const ORIGIN_EXTRA_PROBLEM = {
  path: `Remove the path: ${ONLY_ORIGIN}`,
  query: `Remove the query (?…): ${ONLY_ORIGIN}`,
  fragment: `Remove the fragment (#…): ${ONLY_ORIGIN}`,
  credentials: `Remove the user name and password (…@): ${ONLY_ORIGIN}`,
  wildcard: `Wildcards aren't allowed: ${ONLY_ORIGIN}`
}

export const relativeTime = iso => {
  if (!iso) return null
  const seconds = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000)
  if (seconds < 60) return { text: `${Math.floor(seconds)}s ago`, freshness: 'fresh' }
  if (seconds < 3600) return { text: `${Math.floor(seconds / 60)}m ago`, freshness: seconds < 300 ? 'fresh' : '' }
  if (seconds < 86400) return { text: `${Math.floor(seconds / 3600)}h ago`, freshness: '' }
  return { text: `${Math.floor(seconds / 86400)}d ago`, freshness: 'stale' }
}

// What kind of id an LMS learner id is (plan Task 14A, ruling 9). A SCORM
// student id is the usual case and carries no label; the xAPI kinds do.
const LEARNER_ID_KIND_LABEL = {
  xapi_account: 'xAPI account',
  xapi_mbox: 'xAPI mbox',
  xapi_mbox_sha1sum: 'xAPI mbox hash',
  xapi_openid: 'xAPI OpenID',
  lti13: 'LTI 1.3'
}

export const learnerIdKindLabel = kind => (kind && kind !== 'scorm' ? LEARNER_ID_KIND_LABEL[kind] || kind : null)

// A learner id runs to 4,000 characters (SCORM 2004): the cell shows the
// start of a long one, and its title holds the whole id.
export const LEARNER_ID_SHOWN = 120
export const shortLearnerId = id => (id && id.length > LEARNER_ID_SHOWN ? `${id.slice(0, LEARNER_ID_SHOWN)}…` : id)

// A course's access level as the admin shows it (plan Task 16): "assigned"
// is no level at all, the course is open only to the people and groups it is
// assigned to. Public and private read as they are named.
export const accessLevelLabel = level => (level === 'assigned' ? 'Only assigned' : level)

// What the admin is told when a restrict they asked for did not apply (the
// Add LMS modal's checkbox and the course page's button, plan Task 16): the
// course is connected, but it is not "Only assigned". A restrict that could not
// narrow must not look like success.
const RESTRICT_WARNING = {
  no_learner_group:
    'This course was connected but is still open to everyone in this organization: the connection has no learner group yet. Try again in a few minutes.',
  not_private: 'This course was already not open to everyone in this organization, so nothing changed.',
  public: 'This course is open to all users, so it was not made only assigned.',
  narrowing_failed:
    'This course was connected but is still open to everyone in this organization: the change could not be saved. Try again in a few minutes.'
}

// The warning for a map response, or null when the restrict applied or was
// not asked for (the server sends restrict_applied only when it was).
export const restrictWarning = mapping =>
  mapping && mapping.restrict_applied === false
    ? RESTRICT_WARNING[mapping.restrict_reason] || RESTRICT_WARNING.narrowing_failed
    : null

export const CONNECTION_STATUS_PILL = {
  active: { className: 'success', label: 'Active', pulse: true },
  paused: { className: 'warning', label: 'Paused', pulse: false },
  archived: { className: 'neutral', label: 'Archived', pulse: false }
}
